import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout';
import { useAuth } from '../auth/AuthContext';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfa, resendMfa } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // MFA (second step) state
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [resendNote, setResendNote] = useState('');

  const goNext = () => {
    const dest = location.state?.from || '/dashboard';
    navigate(dest, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email.trim(), password);
      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setCode('');
        setResendNote('');
        return;
      }
      goNext();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaToken, code.trim());
      goNext();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const onResend = async () => {
    setError('');
    setResendNote('');
    setLoading(true);
    try {
      const nextToken = await resendMfa(mfaToken);
      if (nextToken) setMfaToken(nextToken);
      setResendNote('A new code is on its way.');
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not resend the code');
    } finally {
      setLoading(false);
    }
  };

  const backToPassword = () => {
    setMfaToken('');
    setCode('');
    setError('');
    setResendNote('');
  };

  if (mfaToken) {
    return (
      <AuthLayout
        title="Enter your code"
        subtitle={`We emailed a sign-in code to ${email}. Enter it below to continue.`}
        footer={<button type="button" className="auth-link" onClick={backToPassword}>Back to sign in</button>}
      >
        <form className="auth-form" onSubmit={submitCode}>
          {error && <div className="auth-error">{error}</div>}
          {resendNote && <div className="auth-success">{resendNote}</div>}
          <label className="auth-field">
            <span>Sign-in code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              maxLength={6}
              disabled={loading}
              autoFocus
              required
            />
          </label>
          <button type="submit" className="auth-submit" disabled={loading || code.length < 6}>
            {loading ? 'Verifying…' : 'Verify & sign in'}
          </button>
          <button
            type="button"
            className="auth-link"
            onClick={onResend}
            disabled={loading}
            style={{ marginTop: 12, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Didn&apos;t get a code? Resend
          </button>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Welcome back. Enter your details to continue."
      footer={<>Don&apos;t have an account? <Link to="/register">Create one</Link></>}
    >
      <form className="auth-form" onSubmit={submit}>
        {error && <div className="auth-error">{error}</div>}
        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
            autoComplete="email"
            disabled={loading}
            required
          />
        </label>
        <label className="auth-field">
          <div className="auth-field__labelrow">
            <span>Password</span>
            <Link to="/forgot-password" className="auth-link">Forgot password?</Link>
          </div>
          <div className="auth-pw">
            <input
              type={showPw ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Your password"
              autoComplete="current-password"
              disabled={loading}
              required
            />
            <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}>
              {showPw ? 'Hide' : 'Show'}
            </button>
          </div>
        </label>
        <button type="submit" className="auth-submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  );
}
